import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { Camera, RefreshCw, Video, AlertCircle, Eye } from 'lucide-react';
import { HoloCard, CyberButton } from '@/components/CyberComponents';

const Cameras = () => {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadCameras();
    const interval = setInterval(loadCameras, 300000); // 5 minutes (économie API)
    return () => clearInterval(interval);
  }, []);

  const loadCameras = async () => {
    try {
      const response = await api.get('/devices', { params: { device_type: 'camera' } });
      setCameras(response.data);
    } catch (error) {
      console.error('Error loading cameras:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadCameras();
    setTimeout(() => setRefreshing(false), 1000);
  };

  return (
    <Layout title="SURVEILLANCE">
      <div className="h-full grid grid-cols-12 grid-rows-6 gap-2">
        {/* Header Stats */}
        <HoloCard className="col-span-12 row-span-1 flex items-center justify-between p-2">
          <div className="flex items-center space-x-3">
            <Camera className="w-6 h-6 text-cyan-400" style={{ filter: 'drop-shadow(0 0 8px #00f0ff)' }} />
            <div>
              <h2 className="text-sm font-bold neon-cyan font-['Orbitron']">SYSTÈME DE SURVEILLANCE</h2>
              <p className="text-[10px] text-gray-400 font-mono">
                {cameras.length} caméra{cameras.length > 1 ? 's' : ''} configurée{cameras.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <CyberButton
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-3 py-1"
            data-testid="refresh-cameras-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </CyberButton>
        </HoloCard>

        {/* Camera Grid or Empty State */}
        {loading ? (
          <HoloCard className="col-span-12 row-span-5 flex items-center justify-center">
            <div className="text-cyan-400 font-mono animate-pulse">CHARGEMENT DES FLUX...</div>
          </HoloCard>
        ) : cameras.length === 0 ? (
          <HoloCard className="col-span-12 row-span-5 flex flex-col items-center justify-center">
            <div className="relative mb-4">
              <Camera className="w-16 h-16 text-gray-600" />
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse" />
            </div>
            <h3 className="text-lg font-bold neon-cyan font-['Orbitron'] mb-2">AUCUN FLUX DÉTECTÉ</h3>
            <p className="text-xs text-gray-400 font-mono mb-4 text-center max-w-md">
              Ajoutez vos caméras Aqara dans la configuration pour activer la surveillance vidéo.
            </p>
            <CyberButton onClick={() => window.location.href = '/settings'}>
              CONFIGURER AQARA
            </CyberButton>
            
            {/* Info Box */}
            <div className="mt-6 p-3 border border-yellow-500/50 rounded bg-yellow-900/20 max-w-md">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-yellow-200 font-mono">
                  L'intégration Aqara nécessite les clés API Aqara Open Cloud. 
                  Consultez developer.aqara.com pour obtenir vos identifiants.
                </p>
              </div>
            </div>
          </HoloCard>
        ) : (
          <>
            {/* Camera Cards */}
            {cameras.slice(0, 4).map((camera, index) => {
              const isOnline = camera.state?.online !== false;
              const isRecording = camera.state?.recording || false;
              const colSpan = cameras.length === 1 ? 12 : cameras.length === 2 ? 6 : 6;
              const rowSpan = cameras.length <= 2 ? 5 : 2;
              
              return (
                <HoloCard 
                  key={camera.id}
                  className={`col-span-${colSpan} row-span-${rowSpan} flex flex-col overflow-hidden`}
                  data-testid={`camera-card-${camera.id}`}
                >
                  {/* Camera Preview */}
                  <div className="flex-1 relative bg-black/50 flex items-center justify-center min-h-0">
                    {camera.state?.snapshot_url ? (
                      <img
                        src={camera.state.snapshot_url}
                        alt={camera.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center">
                        <Eye className="w-8 h-8 text-gray-600 mx-auto mb-1" />
                        <p className="text-[10px] text-gray-500 font-mono">PAS DE FLUX</p>
                      </div>
                    )}
                    
                    {/* Status Overlay */}
                    <div className="absolute top-2 right-2 flex space-x-1">
                      {isRecording && (
                        <span className="px-2 py-0.5 bg-red-600 text-white text-[9px] font-mono rounded-full flex items-center">
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse mr-1" />
                          REC
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-[9px] font-mono rounded-full ${
                        isOnline ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'
                      }`}>
                        {isOnline ? 'ONLINE' : 'OFFLINE'}
                      </span>
                    </div>
                    
                    {/* Camera Name Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <h4 className="text-xs font-bold text-white font-mono">{camera.name}</h4>
                      {camera.room && (
                        <p className="text-[9px] text-gray-400">{camera.room}</p>
                      )}
                    </div>
                  </div>
                </HoloCard>
              );
            })}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Cameras;